import { useEffect, useMemo, useState } from "react";
import { publicEndpoints } from "@/lib/api";
import { PublicHeader, PublicFooter, ChapterBadge } from "@/components/PublicChrome";
import { toast } from "sonner";
import {
  MapPin,
  Phone,
  Globe,
  Gift,
  Sparkles,
  ExternalLink,
  Users,
  Loader2,
} from "lucide-react";

/* -----------------------------------------------------------
   Nos Amis Producteurs — page publique
   Liste des partenaires locaux (Lot & Quercy) qui rendent
   le jeu gourmand. Cashback 5 % via lien tracké.
   ----------------------------------------------------------- */
export default function Producteurs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Toutes");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicEndpoints
      .producteurs()
      .then((data) => !cancelled && setItems(data || []))
      .catch(() => toast.error("Impossible de charger nos partenaires"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const categories = useMemo(() => {
    const s = new Set(items.map((p) => p.categorie).filter(Boolean));
    return ["Toutes", ...Array.from(s).sort()];
  }, [items]);

  const filtered = useMemo(
    () => (filter === "Toutes" ? items : items.filter((p) => p.categorie === filter)),
    [items, filter]
  );

  const totalCashback = useMemo(() => items.length, [items]);

  const handleClick = (p) => {
    // tracking best-effort — n'attend pas
    publicEndpoints.trackProducteurClic(p.id).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-noir text-parchemin">
      <PublicHeader current="producteurs" />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-or/15">
        <div className="absolute inset-0 opacity-25 pointer-events-none">
          <div className="absolute -top-24 -left-16 w-96 h-96 rounded-full bg-or/20 blur-3xl" />
          <div className="absolute -bottom-20 -right-24 w-96 h-96 rounded-full bg-vert-50/15 blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-14">
          <ChapterBadge>Chapitre gourmand</ChapterBadge>
          <h1 className="mt-4 font-display text-5xl md:text-6xl leading-tight max-w-3xl">
            Nos amis <span className="italic text-or">producteurs.</span>
          </h1>
          <p className="mt-4 text-parchemin/70 max-w-2xl text-base md:text-lg leading-relaxed">
            Le jeu ne serait rien sans eux — vignerons, fromagers, boulangers,
            truffiers du Lot et du Quercy. Retrouvez-les après l&apos;aventure
            et profitez de <span className="text-or font-medium">5 % de cashback</span> sur
            leurs sites en passant par nos liens.
          </p>

          {/* stats */}
          <div className="mt-8 flex flex-wrap gap-6 text-sm font-mono uppercase tracking-widest">
            <div className="flex items-center gap-2 text-parchemin/60">
              <Users className="w-4 h-4 text-or" />
              <span>{totalCashback} partenaires actifs</span>
            </div>
            <div className="flex items-center gap-2 text-parchemin/60">
              <Sparkles className="w-4 h-4 text-vert-50" />
              <span>5 % de cashback partout</span>
            </div>
            <div className="flex items-center gap-2 text-parchemin/60">
              <Gift className="w-4 h-4 text-oxyde" />
              <span>Offres exclusives sur place</span>
            </div>
          </div>
        </div>
      </section>

      {/* Filtres catégorie */}
      {!loading && items.length > 0 && (
        <section className="border-b border-or/10 bg-noir-100/50">
          <div className="max-w-6xl mx-auto px-6 py-4 flex gap-2 overflow-x-auto no-scrollbar">
            {categories.map((c) => {
              const active = c === filter;
              return (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  data-testid={`producteurs-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`shrink-0 px-4 py-2 text-xs font-mono uppercase tracking-widest border rounded-sm transition ${
                    active
                      ? "bg-or/10 border-or text-or"
                      : "border-or/25 text-parchemin/70 hover:border-or/60 hover:text-or"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Grille */}
      <section className="max-w-6xl mx-auto px-6 py-14">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-parchemin/50 gap-2 font-mono uppercase tracking-widest text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-parchemin/50" data-testid="producteurs-empty">
            <MapPin className="w-8 h-8 text-or/40 mx-auto mb-3" />
            <p>Aucun partenaire dans cette catégorie pour l&apos;instant.</p>
          </div>
        ) : (
          <div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="producteurs-grid"
          >
            {filtered.map((p) => (
              <ProducteurCard key={p.id} p={p} onClick={() => handleClick(p)} />
            ))}
          </div>
        )}
      </section>

      {/* CTA cashback */}
      <section className="border-t border-or/15 bg-noir-100/70">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <Gift className="w-8 h-8 text-or mx-auto mb-3" />
          <h2 className="font-display text-3xl md:text-4xl leading-tight">
            Vos courses, <span className="italic text-or">notre voyage.</span>
          </h2>
          <p className="mt-3 text-parchemin/70 leading-relaxed max-w-2xl mx-auto">
            En passant par nos liens partenaires, 5 % du montant vous revient sous
            forme d&apos;avoir pour une prochaine aventure. Une manière simple de
            soutenir les producteurs locaux — et de repartir en balade avec nous.
          </p>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

/* -----------------------------------------------------------
   Card partenaire — thème "Aventure & Temps"
   ----------------------------------------------------------- */
function ProducteurCard({ p, onClick }) {
  return (
    <article
      data-testid={`producteur-card-${p.id}`}
      className="group relative flex flex-col bg-noir-100 border border-or/15 hover:border-or/40 rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1 shadow-[0_20px_50px_-30px_rgba(0,0,0,0.9)]"
    >
      {/* Photo */}
      <div className="relative aspect-[16/10] bg-noir overflow-hidden">
        {p.photoUrl ? (
          <img
            src={p.photoUrl}
            alt={p.nom}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl opacity-60">
            {p.emoji || "🍽️"}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-noir via-transparent to-transparent" />
        {p.pourcentageCashback > 0 && (
          <div className="absolute top-3 right-3 bg-or/95 text-noir px-2.5 py-1 rounded-sm font-mono text-[10px] tracking-widest uppercase">
            Cashback {p.pourcentageCashback}%
          </div>
        )}
        {p.emoji && p.photoUrl && (
          <div className="absolute bottom-3 left-3 text-3xl drop-shadow-lg">
            {p.emoji}
          </div>
        )}
      </div>

      {/* Corps */}
      <div className="flex-1 p-5 flex flex-col">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-vert-50/80">
          {p.categorie}
        </div>
        <h3 className="mt-1 font-display text-xl leading-tight text-parchemin group-hover:text-or transition-colors">
          {p.nom}
        </h3>
        {p.description && (
          <p className="mt-2 text-sm text-parchemin/70 leading-relaxed line-clamp-3">
            {p.description}
          </p>
        )}

        {/* Offre */}
        {p.offre && (
          <div className="mt-3 p-2.5 bg-vert-900/40 border-l-2 border-vert-50 text-xs text-parchemin/90 leading-relaxed italic font-display">
            <div className="flex items-center gap-1 mb-0.5 not-italic text-vert-50 font-mono text-[9px] tracking-widest uppercase">
              <Gift className="w-3 h-3" />
              Offre au jeu
            </div>
            {p.offre}
          </div>
        )}

        {/* Coordonnées */}
        <div className="mt-4 space-y-1.5 text-xs text-parchemin/60">
          {p.adresse && (
            <div className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-or/60 mt-0.5 shrink-0" strokeWidth={1.5} />
              <span>{p.adresse}</span>
            </div>
          )}
          {p.telephone && (
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-or/60 shrink-0" strokeWidth={1.5} />
              <a
                href={`tel:${p.telephone.replace(/\s+/g, "")}`}
                className="hover:text-or transition-colors"
              >
                {p.telephone}
              </a>
            </div>
          )}
          {p.siteWeb && (
            <div className="flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-or/60 shrink-0" strokeWidth={1.5} />
              <a
                href={p.siteWeb}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
                className="hover:text-or transition-colors truncate"
              >
                {new URL(p.siteWeb).hostname.replace(/^www\./, "")}
              </a>
            </div>
          )}
        </div>

        {/* CTA cashback */}
        {p.lienCashback && (
          <a
            href={p.lienCashback}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            data-testid={`producteur-cashback-${p.id}`}
            className="mt-5 flex items-center justify-center gap-2 py-2.5 px-4 bg-or hover:bg-or/90 text-noir font-mono text-xs uppercase tracking-widest rounded-sm transition group/btn"
          >
            <span>Visiter · {p.pourcentageCashback || 5}% cashback</span>
            <ExternalLink className="w-3.5 h-3.5 group-hover/btn:translate-x-0.5 transition-transform" />
          </a>
        )}
      </div>
    </article>
  );
}
